/**
 * ResetPasswordButton — self-contained reset-password affordance for the SMS
 * Profile drawer footer.
 *
 * Lifts the password button + dialog + form out of the Personal tab's
 * `ProfileDetails` ("Password" SectionCard) so it can live in the drawer
 * FOOTER (above Sign Out) via the shared shell's `footerSlot`. Owns its own
 * open state, react-hook-form instance, and `useUpdateUser` mutation — the
 * footer slot just renders it; nothing is threaded through the tab.
 *
 * The password reset path is unchanged from `ProfileDetails`: it reuses
 * `ResetPassowordForm` + `passwordSchema`, calls `useUpdateUser` against the
 * current user's id, and submits `{ password, confirm_password }` on a User
 * PATCH (the serializer validates via `validate_password` then `set_password`).
 */
import { useState } from 'react';
import { Flex, SimpleGrid } from '@chakra-ui/react';
import { HiKey } from 'react-icons/hi2';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, DialogComponent } from '@themes/aegix';
import { useUpdateUser } from '../../hooks/queries';
import type { UserDetailData } from '../../types/userDetails';
import { passwordSchema } from '../../validations/password.validation';
import { ResetPassowordForm } from '../forms/ResetPassowordForm';
import { toaster } from '../ui/toaster';

interface PasswordFormValues {
  password: string;
  confirm_password: string;
}

interface ResetPasswordButtonProps {
  /** Current user id (self). When absent the button is inert. */
  userId?: number | string;
}

export function ResetPasswordButton({ userId }: ResetPasswordButtonProps) {
  const { t } = useTranslation();
  const updateUserMutation = useUpdateUser();
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);

  const passwordMethods = useForm<PasswordFormValues>({
    defaultValues: { password: '', confirm_password: '' },
    resolver: zodResolver(passwordSchema),
    mode: 'onSubmit',
  });

  const openPasswordDialog = () => {
    passwordMethods.reset({ password: '', confirm_password: '' });
    setIsPasswordOpen(true);
  };

  const closePasswordDialog = () => {
    setIsPasswordOpen(false);
  };

  const handleResetPassword = async (values: PasswordFormValues) => {
    if (userId == null) return;
    try {
      await updateUserMutation.mutateAsync({
        id: Number(userId),
        // Same shape used by the admin reset flow (UsersContent.tsx). The
        // serializer cherry-picks `password` + `confirm_password` from the
        // payload via `validate_password` then `set_password`.
        data: values as unknown as UserDetailData,
      });
      toaster.create({
        title: t('profile.passwordUpdated', 'Password updated'),
        type: 'success',
        closable: true,
      });
      closePasswordDialog();
    } catch {
      toaster.create({
        title: t('profile.failedUpdatePassword', 'Failed to update password'),
        type: 'error',
        closable: true,
      });
    }
  };

  return (
    <>
      <Button
        visual="secondary"
        width="100%"
        onClick={openPasswordDialog}
        data-e2e="profileResetPassword"
      >
        <Flex align="center" justifyContent="center" gap={2}>
          <HiKey size={18} />
          {t('profile.resetPassword', 'Reset Password')}
        </Flex>
      </Button>

      <DialogComponent
        isOpen={isPasswordOpen}
        onClose={closePasswordDialog}
        size="sm"
        headingText={t('profile.resetPassword', 'Reset Password')}
        e2eTitle="profile-reset-password-dialog"
      >
        <FormProvider {...passwordMethods}>
          <form
            id="profile-reset-password-form"
            onSubmit={passwordMethods.handleSubmit(handleResetPassword)}
          >
            <ResetPassowordForm />
            <SimpleGrid columns={2} gap={4} width="100%" mt={4}>
              <Button
                visual="secondary"
                type="button"
                onClick={closePasswordDialog}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                visual="primary"
                type="submit"
                form="profile-reset-password-form"
                data-e2e="profileResetPasswordSubmit"
              >
                {t('profile.resetPassword', 'Reset Password')}
              </Button>
            </SimpleGrid>
          </form>
        </FormProvider>
      </DialogComponent>
    </>
  );
}

export default ResetPasswordButton;
